import React from 'react';
import { SubCategoryMapping, SubCategoryLazyRender } from '@draftkings/component-builder';
import { LazyRenderMode, TrackEventFunction } from '@draftkings/sportsbook-common-contracts';
import type { LocalizationConfig } from '@draftkings/sportsbook-labels';
import { TeamLogoProps } from '@draftkings/scoreboards';
import { usePlayerPageProps } from '../../contexts/PlayerPageContext/usePlayerPageProps';
import { ChunkedSubCategoriesProps } from '../../contracts/state';
import { lazyRendering } from '../../helpers';

import './index.scss';

type MarketboardProps = {
    chunkedSubCategories: ChunkedSubCategoriesProps[];
    toggleIsExpanded: (subcategoryId: string) => void;
    trackEvent: TrackEventFunction;
    consumerVersion: string;
    getTeamLogo: (props: TeamLogoProps) => JSX.Element;
    lazyRenderMode: LazyRenderMode;
    localizationConfig: LocalizationConfig;
    locale: string;
};

export const Marketboard = React.memo(
    ({
        chunkedSubCategories,
        children,
        toggleIsExpanded,
        trackEvent,
        consumerVersion,
        getTeamLogo,
        lazyRenderMode,
        localizationConfig,
        locale
    }: React.PropsWithChildren<MarketboardProps>) => {
        const marketboardRef = React.useRef<HTMLDivElement>(null);
        const { playerPageConfig, getPlayerLink, onPlayerLinkClick } = usePlayerPageProps();

        return (
            <div ref={marketboardRef} className="marketboard-layout">
                <div className="marketboard-layout__content">
                    {children}
                    <div className="marketboard-layout__content__main">
                        {chunkedSubCategories.map((subcategories) => {
                            const totalMarketsLength = subcategories.chunkedSubCategories.reduce(
                                (total, subcat) => total + subcat.marketsCells.length,
                                0
                            );
                            return (
                                <SubCategoryLazyRender
                                    key={subcategories.id}
                                    marketsLength={totalMarketsLength}
                                    lazyRenderMode={lazyRenderMode}
                                    {...lazyRendering}
                                >
                                    {subcategories.chunkedSubCategories.map((subcategory, i) => {
                                        return (
                                            <SubCategoryMapping
                                                key={`subcategory-${subcategory.id}-${i}`}
                                                title={subcategory.name}
                                                showSgpBadge={subcategory.showSgpBadge}
                                                localizationConfig={localizationConfig}
                                                locale={locale}
                                                marketData={{
                                                    consumerVersion: consumerVersion,
                                                    widgetVersion: APP_VERSION,
                                                    marketsCells: subcategory.marketsCells,
                                                    playerPageConfig: playerPageConfig,
                                                    trackEvent: trackEvent,
                                                    getPlayerLink: getPlayerLink,
                                                    onPlayerLinkClick: onPlayerLinkClick,
                                                    marketboardRef,
                                                    getTeamLogo: getTeamLogo
                                                }}
                                                isExpanded={subcategory.isExpanded}
                                                contentId={subcategory.id}
                                                animationConfig={{
                                                    duration: 300,
                                                    ease: 'ease-in-out'
                                                }}
                                                onToggleExpandClick={() => {
                                                    toggleIsExpanded(subcategory.id);
                                                }}
                                            />
                                        );
                                    })}
                                </SubCategoryLazyRender>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }
);
